import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, CheckCircle2, Calendar, Download, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Verify() {
    const { caseNumber } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Defensive: validate the response is a non-empty object before
        // accepting it. Some intermediaries (corporate proxies, captive
        // portals) return 200 with HTML — we'd rather show the retry
        // screen than crash on `data.case_number`.
        let cancelled = false;
        axios.get(`${API}/verify/${caseNumber}`)
            .then(({ data: payload }) => {
                if (cancelled) return;
                if (payload && typeof payload === "object" && payload.case_number) {
                    setData(payload);
                } else {
                    setErr("Verification failed");
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setErr(e?.response?.status === 404 ? "Not found" : "Verification failed");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [caseNumber]);

    if (loading) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-400" size={28} />
            </div>
        );
    }

    if (err === "Not found") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-5">
                <div className="max-w-md text-center" data-testid="verify-not-found">
                    <div className="h-16 w-16 mx-auto rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-5"><ShieldAlert size={28} /></div>
                    <h1 className="font-display text-3xl tracking-tight text-slate-900">Warranty not found</h1>
                    <p className="text-slate-600 mt-3 text-[14.5px]">We couldn't find a warranty for case <span className="font-mono-tr text-slate-900">{caseNumber}</span>. Please double-check the QR or contact your dentist.</p>
                    <Link to="/" className="tr-btn-ghost mt-6 inline-flex">Back to home</Link>
                </div>
            </div>
        );
    }

    // Defensive: when the API call failed (network / 500 / CORS) OR the
    // response was unexpectedly empty, render a graceful retry screen
    // instead of crashing the page. Without this guard, the main render
    // branch below would `data.case_number` against `null` and throw,
    // which surfaces as the global "Something broke" error boundary —
    // we'd rather show the patient a clear "try again" message.
    if (!data) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-5">
                <div className="max-w-md text-center" data-testid="verify-error">
                    <div className="h-16 w-16 mx-auto rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5"><ShieldAlert size={28} /></div>
                    <h1 className="font-display text-3xl tracking-tight text-slate-900">Verification temporarily unavailable</h1>
                    <p className="text-slate-600 mt-3 text-[14.5px]">We couldn't reach the warranty service for case <span className="font-mono-tr text-slate-900">{caseNumber}</span> right now. Please try again in a moment.</p>
                    <div className="mt-6 flex items-center justify-center gap-2">
                        <button
                            onClick={() => window.location.reload()}
                            data-testid="verify-retry"
                            className="tr-btn"
                        >
                            Try again
                        </button>
                        <Link to="/" className="tr-btn-ghost">Back to home</Link>
                    </div>
                    <p className="mt-5 text-[12px] text-slate-400">If it keeps happening, email <a href="mailto:orders@toothrocket.com" className="text-cyan-700 underline">orders@toothrocket.com</a>.</p>
                </div>
            </div>
        );
    }

    const valid = data.valid;
    return (
        <div data-testid="verify-page" className="min-h-[80vh] bg-slate-50/60">
            <div className="max-w-[760px] mx-auto px-5 lg:px-8 py-12 lg:py-20">
                <div className={`rounded-3xl overflow-hidden border ${valid ? "border-emerald-200" : "border-amber-200"} bg-white shadow-sm`}>
                    {/* Hero */}
                    <div className={`p-8 lg:p-10 ${valid ? "bg-gradient-to-br from-emerald-50 via-white to-cyan-50" : "bg-gradient-to-br from-amber-50 via-white to-white"}`}>
                        <div className="flex items-start gap-4">
                            <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${valid ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
                                {valid ? <ShieldCheck size={26} /> : <ShieldAlert size={26} />}
                            </div>
                            <div>
                                <div className="font-mono-tr text-[11px] tracking-[0.22em] uppercase text-slate-500">· Toothrocket warranty · verified</div>
                                <h1 className="font-display font-light text-3xl lg:text-4xl text-slate-900 tracking-tight mt-1">
                                    {valid ? "Warranty Valid" : "Pending Activation"}
                                </h1>
                                {valid && (
                                    <div className="flex items-center gap-1.5 mt-2 text-emerald-700 text-[13.5px]">
                                        <CheckCircle2 size={15} /> {data.years}-year clinical coverage in force
                                    </div>
                                )}
                                {!valid && data?.reason && (
                                    <div className="mt-2 text-amber-800 text-[13.5px]">{data.reason}</div>
                                )}
                            </div>
                        </div>

                        <div className="mt-7 grid sm:grid-cols-2 gap-3">
                            <Field label="Restoration">{data.product || "—"}</Field>
                            <Field label="Case ID"><span className="font-mono-tr text-slate-900">{data.case_number}</span></Field>
                            <Field label="Issued on" icon={Calendar}>{data.issued_on || "Pending acceptance"}</Field>
                            <Field label="Expires on" icon={Calendar}>{data.expires_on || "—"}</Field>
                            <Field label="Clinic">{data.clinic_name || "—"}</Field>
                            <Field label="Patient">{data.patient}</Field>
                        </div>
                    </div>

                    {/* Coverage */}
                    {data?.warranty_terms && (
                        <div className="p-8 lg:p-10 border-t border-slate-100">
                            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-4">· What's covered</div>
                            <ul className="space-y-3">
                                {data.warranty_terms.map((t, i) => (
                                    <li key={`term-${i}-${String(t).slice(0, 24)}`} className="flex items-start gap-3 text-[14.5px] text-slate-700 leading-relaxed">
                                        <CheckCircle2 size={17} className="text-emerald-600 mt-0.5 shrink-0" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* CTAs */}
                    <div className="p-8 lg:p-10 bg-slate-50/60 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                        <Link to={`/track/${data.case_number}`} className="tr-btn-ghost" data-testid="verify-track-link">
                            View case status <ArrowUpRight size={15} />
                        </Link>
                        <a href={`${API}/cases/${data.case_number}/warranty.pdf`} target="_blank" rel="noreferrer" className="tr-btn" data-testid="verify-pdf">
                            <Download size={15} /> Download warranty card
                        </a>
                    </div>
                </div>

                <div className="text-center mt-8 text-[13px] text-slate-500">
                    Issued by <Link to="/" className="text-slate-900 hover:text-cyan-700 font-medium">Toothrocket Labs</Link> · Doha · Qatar
                </div>
            </div>
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <div className="rounded-xl bg-white border border-slate-200 px-4 py-3">
            <div className="font-mono-tr text-[10.5px] tracking-[0.2em] uppercase text-slate-500 flex items-center gap-1.5">
                {Icon && <Icon size={11} />}
                {label}
            </div>
            <div className="mt-1 text-[14px] text-slate-900 font-medium truncate">{children}</div>
        </div>
    );
}
